<script>
  import { onMount, afterUpdate, onDestroy } from "svelte";
  import Agent_ChartAnalysis from "./Agent_ChartAnalysis.svelte";
  import { 
    evidenceStore, 
    saveEvidence, 
    removeSavedEvidence,
    setCurrentEvidence,
    clearCurrentEvidence 
  } from "../stores/evidenceStore";
  import { chartAgentInstructions } from "../stores/agentInstructionsStore";
  import { chatStore, resetChat } from "../stores/chatStore";
  import { createEventDispatcher } from 'svelte';
  import { selectionStore } from '../stores/selectionStore';
  import ChartSelection from './ChartSelection.svelte';
  import { get } from 'svelte/store';
  import { promptStore } from '../stores/promptStore';
  import { storyStore } from '../stores/storyStore';
  import Zoomist from 'zoomist';
  import { slide } from 'svelte/transition'; // Add slide transition
  import { modalStore } from '../stores/modalStore.js';

  export let evidence;
  export let isOpen = false;
  export let closeModal;
  
  const dispatch = createEventDispatcher();
  let activeTab = 'discuss';
  let savedEvidence = [];
  let canvasContainer;
  let canvas;
  let selectionActive = false;
  let selectedRegion = null;
  let imageElement;
  let bigQuestion;
  let promptDetails;
  let chatKey;

  let zoomInstance;
  let zoomContainer;
  let isZoomInitialized = false;
  
  // Add state for content header
  let isContentExpanded = false;
  let isEvidenceSaved = false;

  evidenceStore.subscribe(store => {
    // Only update if no evidence is provided as prop
    if (!evidence) {
      evidence = store.currentEvidence;
    }
    savedEvidence = store.savedEvidence || [];
    
    // Update save state based on evidence
    isEvidenceSaved = evidence ? savedEvidence.some(e => e._id === evidence._id) : false;
  });

  storyStore.subscribe(store => {
    bigQuestion = store.bigQuestion;
  });

  promptStore.subscribe(store => {
    promptDetails = store.selectedPromptDetails;
  });

  // Set current evidence when modal opens, clear when it closes
  $: if (isOpen && evidence) {
    setCurrentEvidence(evidence);
    // Initialize story store if needed
    const currentStory = get(storyStore);
    if (!currentStory?._id) {
      // Create a temporary story for research mode
      storyStore.update(state => ({
        ...state,
        _id: 'research-mode',
        bigQuestion: evidence.bigQuestion || 'Research Mode',
        title: 'Research Mode',
        slides: [],
        template: 'none',
        isDirty: false,
        isSaving: false
      }));
    }
  } else {
    clearCurrentEvidence();
  }

  $: isEvidenceSaved = savedEvidence.some(e => e._id === evidence._id);

  // Subscribe to agent instructions changes
  $: {
    $chartAgentInstructions; // This creates a dependency
    if (activeTab === 'discuss') {
      resetChat(); // Reset chat when instructions change
    }
  }

  // Subscribe to chat store to get the key
  chatStore.subscribe(state => {
    chatKey = state.key;
  });

  function handleAddEvidence(event) {
    event.stopPropagation();
    dispatch('addToCanvas', { evidence });
  }

  function handleSaveEvidence(event) {
    if (event) event.stopPropagation();
    if (!evidence) return;
    
    const isAlreadySaved = savedEvidence.some(e => e._id === evidence._id);
    if (isAlreadySaved) {
      removeSavedEvidence(evidence._id);
    } else {
      saveEvidence(evidence);
    }
    isEvidenceSaved = !isEvidenceSaved;
  }

  function initializeCanvas() {
    if (!canvasContainer || !window.fabric) return;

    // Get the image element
    const imageElement = canvasContainer.querySelector('.evidence-image');
    if (!imageElement) return;

    // Get the actual displayed dimensions of the image
    const imageBounds = imageElement.getBoundingClientRect();
    const containerRect = canvasContainer.getBoundingClientRect();

    // Calculate the offset to center the canvas over the image
    const offsetX = (containerRect.width - imageBounds.width) / 2;
    const offsetY = (containerRect.height - imageBounds.height) / 2;

    // Destroy existing canvas if it exists
    if (canvas) {
      canvas.dispose();
    }

    // Create new canvas with image dimensions
    canvas = new window.fabric.Canvas('selection-canvas', {
      width: imageBounds.width,
      height: imageBounds.height,
      selection: false,
      renderOnAddRemove: true,
      backgroundColor: 'transparent'
    });

    // Position the canvas absolutely over the image
    const canvasElement = document.getElementById('selection-canvas');
    if (canvasElement) {
      canvasElement.style.position = 'absolute';
      canvasElement.style.left = `${offsetX}px`;
      canvasElement.style.top = `${offsetY}px`;
      canvasElement.style.zIndex = '100';
    }

    // Add selection rectangle capability
    let isDown = false;
    let startX = 0;
    let startY = 0;
    let selectionRect = null;

    canvas.on('mouse:down', (o) => {
      if (!selectionActive) return;
      isDown = true;
      const pointer = canvas.getPointer(o.e);
      startX = pointer.x;
      startY = pointer.y;

      selectionRect = new window.fabric.Rect({
        left: startX,
        top: startY,
        width: 0,
        height: 0,
        fill: 'rgba(37, 99, 235, 0.1)',
        stroke: '#2563eb',
        strokeWidth: 2,
        selectable: false,
        evented: false
      });

      canvas.add(selectionRect);
      canvas.renderAll();
    });

    canvas.on('mouse:move', (o) => {
      if (!isDown || !selectionActive) return;
      const pointer = canvas.getPointer(o.e);

      if (selectionRect) {
        const width = Math.abs(pointer.x - startX);
        const height = Math.abs(pointer.y - startY);
        selectionRect.set({
          width: width,
          height: height,
          left: Math.min(startX, pointer.x),
          top: Math.min(startY, pointer.y)
        });
        canvas.renderAll();
      }
    });

    canvas.on('mouse:up', () => {
      if (!selectionActive) return;
      isDown = false;
      if (selectionRect && selectionRect.width > 10 && selectionRect.height > 10) {
        // Add debug logging
        console.log('Canvas Selection:', {
            left: selectionRect.left,
            top: selectionRect.top,
            width: selectionRect.width,
            height: selectionRect.height
        });
        
        // Get canvas dimensions
        console.log('Canvas Dimensions:', {
            width: canvas.width,
            height: canvas.height,
            scale: canvas.getZoom()
        });

        selectedRegion = {
            x: selectionRect.left,
            y: selectionRect.top,
            width: selectionRect.width,
            height: selectionRect.height
        };
      } else {
        // Remove too small selections
        canvas.remove(selectionRect);
        selectedRegion = null;
      }
      canvas.renderAll();
    });
  }

  function toggleSelection() {
    if ($selectionStore.isSelecting) {
      selectionStore.clearSelection();
    } else {
      selectionStore.startSelection(evidence);
    }
  }

  // Initialize canvas when modal opens
  $: if (isOpen) {
    setTimeout(() => {
      initializeCanvas();
    }, 100); // Small delay to ensure container is ready
  }

  // Cleanup on modal close
  $: if (!isOpen && canvas) {
    canvas.dispose();
    canvas = null;
    selectionActive = false;
    selectedRegion = null;
  }

  // Handle window resize
  function handleResize() {
    if (isOpen) {
      initializeCanvas();
    }
  }

  onMount(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (canvas) {
        canvas.dispose();
      }
    };
  });

  function validateSelection(selection, imageDimensions) {
    const { naturalWidth, naturalHeight } = imageDimensions;
    
    // Ensure coordinates are within image bounds
    const validatedSelection = {
        x: Math.max(0, Math.min(selection.x, naturalWidth)),
        y: Math.max(0, Math.min(selection.y, naturalHeight)),
        width: Math.min(selection.width, naturalWidth - selection.x),
        height: Math.min(selection.height, naturalHeight - selection.y)
    };
    
    console.log('Validated Selection:', {
        original: selection,
        validated: validatedSelection,
        imageDimensions: { naturalWidth, naturalHeight }
    });
    
    return validatedSelection;
  }

  function handleSelectionComplete(event) {
    const { selection } = event.detail;
    console.log('Raw Selection:', selection);
    
    // Ensure we have valid image dimensions
    const naturalWidth = imageElement?.naturalWidth || 0;
    const naturalHeight = imageElement?.naturalHeight || 0;
    
    console.log('Image Dimensions:', {
        naturalWidth,
        naturalHeight,
        clientWidth: imageElement?.clientWidth || 0,
        clientHeight: imageElement?.clientHeight || 0
    });

    if (!naturalWidth || !naturalHeight) {
        console.error('Invalid image dimensions');
        return;
    }

    // Convert pixel coordinates to percentages
    const regionPercentages = {
        x: (selection.x / naturalWidth) * 100,
        y: (selection.y / naturalHeight) * 100,
        width: (selection.width / naturalWidth) * 100,
        height: (selection.height / naturalHeight) * 100
    };
    
    console.log('Calculated Region Percentages:', regionPercentages);

    activeTab = 'discuss';
    
    if (typeof window !== 'undefined' && evidence.mediaURL) {
        window.dispatchEvent(new CustomEvent('askAgent', {
            detail: {
                question: "Can you help me understand what's happening in this selected region of the graph?",
                context: {
                    region: regionPercentages,  // Now in percentages
                    bigQuestion: evidence.bigQuestion || 'Default Question',
                    description: evidence.description || 'No description available',
                    imageData: {
                        src: evidence.mediaURL,  // No fallback needed
                        naturalWidth,
                        naturalHeight,
                        displayWidth: imageElement?.clientWidth || 0,
                        displayHeight: imageElement?.clientHeight || 0
                    }
                }
            }
        }));
    }

    // Reset selection state
    selectionStore.clearSelection();
  }

  // Make sure to clear current evidence when component is destroyed
  onDestroy(() => {
    clearCurrentEvidence();
    selectionStore.reset();
  });

  // Add a resize observer to handle image size changes
  onMount(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (isOpen) {
        // Small delay to ensure image has finished resizing
        setTimeout(initializeCanvas, 100);
      }
    });

    if (canvasContainer) {
      resizeObserver.observe(canvasContainer);
    }

    return () => {
      resizeObserver.disconnect();
    };
  });

  // Add debug logging
  $: console.log('EvidenceModal props:', { evidence, isOpen });

  function drawDebugOverlay(selection) {
    // Add a temporary debug rectangle
    const debugRect = new window.fabric.Rect({
        left: selection.x,
        top: selection.y,
        width: selection.width,
        height: selection.height,
        fill: 'transparent',
        stroke: 'red',
        strokeWidth: 1,
        selectable: false,
        evented: false
    });
    
    canvas.add(debugRect);
    setTimeout(() => {
        canvas.remove(debugRect);
        canvas.renderAll();
    }, 2000); // Remove after 2 seconds
  }

  onMount(() => {
    // Delay initialization to ensure the DOM is ready
    setTimeout(() => {
      if (zoomContainer && !isZoomInitialized) {
        console.log('Initializing Zoomist...');
        initializeZoomist();
      }
    }, 300); // Increased delay to ensure everything is loaded

    return () => {
      destroyZoomist();
    };
  });

  function initializeZoomist() {
    if (!zoomContainer || isZoomInitialized) return;
    
    try {
      // Check if Zoomist is available
      if (typeof Zoomist === 'undefined') {
        console.error('Zoomist library not loaded');
        return;
      }

      // Wait for image to be loaded
      if (imageElement && !imageElement.complete) {
        imageElement.addEventListener('load', () => {
          initializeZoomistInstance();
        });
        return;
      }

      initializeZoomistInstance();
    } catch (error) {
      console.error("Error initializing Zoomist:", error);
      isZoomInitialized = false;
    }
  }

  function initializeZoomistInstance() {
    try {
      if (zoomInstance) {
        destroyZoomist();
      }

      // Following the exact structure from documentation
      zoomInstance = new Zoomist(zoomContainer, {
        maxScale: 4,
        bounds: true,
        slider: false,
        zoomer: {
          // Define custom elements for zoomer
          el: '.image-toolbar',
          inEl: '.zoom-in-btn',
          outEl: '.zoom-out-btn',
          resetEl: '.zoom-reset-btn',
          disabledClass: 'zoomer-disabled'
        },
        minScale: 0.5,
        draggable: true,
        wheelable: true,
        pinchable: true,
        zoomRatio: 0.2
      });

      isZoomInitialized = true;
      console.log("Zoomist initialized successfully");
    } catch (error) {
      console.error("Error creating Zoomist instance:", error);
      isZoomInitialized = false;
    }
  }

  function destroyZoomist() {
    if (zoomInstance) {
      try {
        zoomInstance.destroy();
      } catch (error) {
        console.error("Error destroying Zoomist:", error);
      }
      zoomInstance = null;
      isZoomInitialized = false;
    }
  }

  // Update the reactive statement for initialization
  $: if (isOpen && !isZoomInitialized) {
    setTimeout(() => {
      if (zoomContainer && !isZoomInitialized) {
        console.log('Initializing Zoomist...');
        initializeZoomist();
      }
    }, 300);
  } else if (!isOpen) {
    destroyZoomist();
  }

  // Update image load handler
  function handleImageLoad() {
    console.log('Image loaded');
    if (!isZoomInitialized) {
      initializeZoomist();
    }
  }

  // Add effect to manage modal state
  $: if (isOpen) {
    modalStore.openModal('evidence');
  } else {
    modalStore.closeModal('evidence');
  }

  // Update the close button handler
  function handleClose() {
    modalStore.closeModal('evidence');
    if (closeModal) closeModal();
  }

</script>

{#if evidence && Object.keys(evidence).length > 0}
  <!-- Debug output -->
  <pre style="display: none;">{JSON.stringify(evidence, null, 2)}</pre>
  <div class="modal-content">
    <button class="close-button" on:click={handleClose} aria-label="Close modal">
      <i class="fas fa-times"></i>
    </button>
    
    <div class="modal-grid">
      <!-- Left Column - Image -->
      <div class="image-column">
        <!-- Evidence content header - MOVED FROM RIGHT SIDE -->
        {#if evidence}
          <div class="content-header">
            <div class="header-main">
              <h2>{evidence.label_easy || evidence.label || 'Untitled Chart'}</h2>
              <div class="header-controls">
                <button 
                  class="expand-button"
                  on:click={() => isContentExpanded = !isContentExpanded}
                  aria-expanded={isContentExpanded}
                  aria-label={isContentExpanded ? 'Collapse description' : 'Expand description'}
                >
                  <i class="fas fa-chevron-{isContentExpanded ? 'up' : 'down'}"></i>
                </button>
                <button 
                  class="save-button {isEvidenceSaved ? 'saved' : ''}"
                  on:click={handleSaveEvidence}
                  title={isEvidenceSaved ? "Remove from saved" : "Save data chart"}
                >
                  <i class="fas fa-bookmark"></i>
                </button>
              </div>
            </div>
            {#if isContentExpanded}
              <div class="header-description" transition:slide>
                <div class="description-content">
                  <div class="description-section">
                    <h3>Description</h3>
                    <p>{evidence.description_easy || evidence.description || 'No description available'}</p>
                  </div>
                  {#if evidence.sourceURL}
                    <div class="source-info">
                      <span class="source-label">Source:</span>
                      <a href={evidence.sourceURL} target="_blank" rel="noopener noreferrer">
                        {evidence.sourceLabel || evidence.sourceURL}
                      </a>
                    </div>
                  {/if}
                </div>
              </div>
            {/if}
          </div>
        {/if}

        <!-- Image toolbar -->
        <div class="image-toolbar">
          <div class="toolbar-group zoom-controls">
            <button class="toolbar-button zoom-in-btn" title="Zoom In">
              <i class="fas fa-search-plus"></i>
            </button>
            <button class="toolbar-button zoom-out-btn" title="Zoom Out">
              <i class="fas fa-search-minus"></i>
            </button>
            <button class="toolbar-button zoom-reset-btn" title="Reset View">
              <i class="fas fa-compress-arrows-alt"></i>
            </button>
          </div>
        </div>

        <!-- Image container with Zoomist -->
        <div bind:this={zoomContainer} class="zoomist-container">
          <div class="zoomist-wrapper">
            <div class="zoomist-image">
              <img 
                bind:this={imageElement}
                src={evidence.source === 'OWID' && evidence.mediaURL ? evidence.mediaURL : evidence.s3URL}
                alt={evidence.label || 'Chart visualization'}
                class="evidence-image"
                draggable="false"
                on:error={() => console.error('Image failed to load:', evidence.source === 'OWID' ? evidence.mediaURL : evidence.s3URL)}
                on:load={handleImageLoad}
              />
            </div>
          </div>
          
          <!-- Chart selection component -->
          <ChartSelection 
            {imageElement}
            on:selectionComplete={handleSelectionComplete}
          />
          
          <!-- Zoom instructions -->
          <div class="zoom-hint">
            <i class="fas fa-info-circle"></i>
            <span>Scroll to zoom • Drag to pan</span>
          </div>
        </div>
      </div>
      
      <!-- Right Column - Content -->
      <div class="content-column">
        <Agent_ChartAnalysis key={chatKey} />
      </div>
    </div>
  </div>
  
{:else}
  <div class="modal-content loading-state">
    <button class="close-button" on:click={handleClose}>
      <i class="fas fa-times"></i>
    </button>
    <div class="loading-indicator">
      <i class="fas fa-spinner fa-pulse"></i>
      <p>Loading visualization...</p>
    </div>
  </div>
{/if}

<style>
  /* Modern minimalist styling */
  .modal-content {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background-color: #fff;
  }

  .modal-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    height: 100%;
    overflow: hidden;
    flex: 1;
  }

  /* Left column - Image */
  .image-column {
    height: 100%;
    border-right: 1px solid #eaeaea;
    background: #ffffff;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  /* Content header styles - ADDED */
  .content-header {
    background: #fff;
    padding: 1rem;
    border-bottom: 1px solid #eaeaea;
    width: 100%;
  }

  .header-main {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .header-main h2 {
    margin: 0;
    font-size: 1.25rem;
    color: #1e293b;
    font-weight: 600;
  }

  .header-controls {
    display: flex;
    gap: 0.5rem;
  }

  .expand-button,
  .save-button {
    background: none;
    border: none;
    color: #64748b;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .expand-button:hover,
  .save-button:hover {
    background-color: #f1f5f9;
    color: #2563eb;
  }

  .save-button.saved {
    color: #3b82f6;
  }

  .header-description {
    padding: 1rem 0 0.5rem;
  }

  .description-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    font-size: 0.9rem;
  }

  .description-section h3 {
    margin: 0 0 0.5rem 0;
    font-size: 0.95rem;
    color: #1e293b;
    font-weight: 600;
  }

  .description-section p {
    margin: 0;
    color: #475569;
    line-height: 1.5;
  }

  .source-info {
    margin-top: 0.5rem;
    font-size: 0.875rem;
    color: #64748b;
  }

  .source-label {
    font-weight: 600;
  }

  .source-info a {
    color: #3b82f6;
    text-decoration: none;
  }

  /* Toolbar styling */
  .image-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: #ffffff;
    border-bottom: 1px solid #eaeaea;
    width: 100%;
    box-shadow: 0 1px 2px rgba(0,0,0,0.03);
    position: relative;
  }

  .toolbar-group {
    display: flex;
    gap: 8px;
    align-items: center;
  }
  
  .zoom-controls {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  
  .tool-label {
    font-size: 0.85rem;
    color: #64748b;
    font-weight: 500;
  }

  .toolbar-divider {
    width: 1px;
    height: 24px;
    background: #eaeaea;
    margin: 0 4px;
  }

  .toolbar-button {
    width: 36px;
    height: 36px;
    border: none;
    background: #f5f5f7;
    color: #333;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: all 0.2s ease;
    border-radius: 8px;
  }
  
  .toolbar-button.text-button {
    width: auto;
    padding: 0 12px;
    gap: 8px;
  }
  
  .toolbar-button.text-button span {
    font-size: 0.85rem;
    font-weight: 500;
    white-space: nowrap;
  }

  .toolbar-button:hover:not(.zoomer-disabled) {
    background: #ececf0;
    color: #2563eb;
  }

  .toolbar-button.active {
    background: #2563eb;
    color: white;
  }

  .toolbar-button.zoomer-disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* Zoomist container */
  .zoomist-container {
    flex: 1;
    position: relative;
    background: #fafafa;
    overflow: hidden;
  }

  .zoomist-wrapper {
    background: transparent;
  }

  .zoomist-image {
    width: 100%;
    height: 100%;
  }

  .zoomist-image img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  /* Remove old zoomer styles */
  :global(.zoomist-zoomer) {
    display: none;
  }

  /* Zoom hint */
  .zoom-hint {
    position: absolute;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(255, 255, 255, 0.9);
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 0.85rem;
    color: #666;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .zoomist-container:hover .zoom-hint {
    opacity: 1;
  }

  /* Right column - Content */
  .content-column {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    background: #121212;
    position: relative;
    padding: 0;
  }

  /* Close button */
  .close-button {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 10001;
    background: #ffffff;
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    color: #666;
  }

  .close-button:hover {
    background: #f5f5f7;
    color: #333;
    transform: scale(1.05);
  }

  /* Loading state */
  .loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .loading-indicator {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    color: #666;
  }

  .loading-indicator i {
    font-size: 2rem;
    color: #2563eb;
  }

  .loading-indicator p {
    margin: 0;
    font-size: 1rem;
  }

  /* Chat container adjustments */
  .chat-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 100%;
    background-color: #f8fafc;
    overflow: hidden;
    position: relative;
  }

  .current-exchange {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 2rem;
    overflow-y: auto;
    height: 100%;
    min-height: 0;
    justify-content: flex-start !important; /* Force top alignment */
    align-items: stretch;
  }

  .message {
    margin-bottom: 1.5rem;
    max-width: 90%;
    width: fit-content;
  }

  .message.user {
    align-self: flex-end;
  }

  .message.ai {
    align-self: flex-start;
  }

  .message:last-child {
    margin-bottom: 2rem;
  }

  .minimalist-chat {
    flex: 1;
    display: flex;
    flex-direction: column;
    position: relative;
    margin: 0px !important;
    border-radius: 0;
    background: #121212;
    overflow: hidden;
    height: 100%;
    box-shadow: none;
  }

  .message-content {
    font-size: 0.95rem;
    line-height: 1.5;
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-wrap: break-word;
    padding: 0.75rem 1rem;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.05);
    max-width: 100%;
  }

  .message.user .message-content {
    background: rgba(96, 165, 250, 0.1);
    color: #60A5FA;
  }

  .message.ai .message-content {
    background: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.85);
  }

  /* Ensure chat history also scrolls properly */
  .chat-history {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.95);
    display: flex;
    flex-direction: column;
    padding: 2rem;
    overflow-y: auto;
    transform: translateX(100%);
    transition: transform 0.3s ease;
    z-index: 5;
    min-height: 0;
    align-items: flex-start;
  }

  .chat-history .message {
    width: 100%;
    max-width: 90%;
  }

  /* Add some spacing at the top and bottom of the scrollable area */
  .current-exchange::before,
  .current-exchange::after {
    content: '';
    flex: 0 0 1rem;
  }

  .chat-history::before,
  .chat-history::after {
    content: '';
    flex: 0 0 1rem;
  }

  /* Add responsive text sizing */
  @media (max-width: 768px) {
    .message-content {
      font-size: 0.9rem;
    }
  }

  @media (max-width: 480px) {
    .message-content {
      font-size: 0.85rem;
    }
  }

  /* Add responsive styling */
  @media (max-width: 768px) {
    .image-toolbar {
      flex-wrap: wrap;
      gap: 8px;
    }
    
    .toolbar-divider {
      display: none;
    }
    
    .toolbar-group {
      flex: 1;
      justify-content: center;
    }
    
    .toolbar-button.text-button {
      flex: 1;
      justify-content: center;
    }
  }
  
  @media (max-width: 480px) {
    .tool-label {
      display: none;
    }
    
    .toolbar-button.text-button span {
      font-size: 0.75rem;
    }
    
    .toolbar-group {
      flex-wrap: wrap;
    }
  }
</style>
